import { useCallback, useEffect, useState } from 'react';

interface CancerInterestInterstitialProps {
  onNext: () => void;
  selectedCancer?: string;
}

// ─── Cancer grid data ───────────────────────────────────────────────────────

const CANCER_TYPES = [
  { slug: 'bladder', label: 'Bladder', icon: 'Cancer-Urinary' },
  { slug: 'brain', label: 'Brain', icon: 'Cancer-Brain' },
  { slug: 'breast', label: 'Breast', icon: 'Cancer-Breast' },
  { slug: 'cervical', label: 'Cervical', icon: 'Cancer-Cervical' },
  { slug: 'colon', label: 'Colon', icon: 'Cancer-Colon' },
  { slug: 'esophageal', label: 'Esophageal', icon: 'Cancer-Esophageal' },
  { slug: 'head-and-neck', label: 'Head & Neck', icon: 'Cancer-Oral' },
  { slug: 'kidney', label: 'Kidney', icon: 'Cancer-Kidney' },
  { slug: 'leukemia', label: 'Leukemia', icon: 'Cancer-Leukemia' },
  { slug: 'liver', label: 'Liver', icon: 'Cancer-Liver' },
  { slug: 'lung', label: 'Lung', icon: 'Cancer-Lung' },
  { slug: 'lymphoma', label: 'Lymphoma', icon: 'Cancer-Lymphoma' },
  { slug: 'melanoma', label: 'Melanoma', icon: 'Cancer-Melanoma' },
  { slug: 'multiple-myeloma', label: 'Myeloma', icon: 'Cancer-Myeloma' },
  { slug: 'ovarian', label: 'Ovarian', icon: 'Cancer-Ovarian' },
  { slug: 'pancreatic', label: 'Pancreatic', icon: 'Cancer-Pancreas' },
  { slug: 'prostate', label: 'Prostate', icon: 'Cancer-Prostate' },
  { slug: 'stomach', label: 'Stomach', icon: 'Cancer-Stomach' },
  { slug: 'testicular', label: 'Testicular', icon: 'Cancer-Testicular' },
  { slug: 'thyroid', label: 'Thyroid', icon: 'Cancer-Thyroid' },
  { slug: 'uterine', label: 'Uterine', icon: 'Cancer-Uterine' },
];

// ─── Risk driver data ───────────────────────────────────────────────────────

const RISK_DRIVERS = [
  { label: 'Height', delay: 0 },
  { label: 'Alcohol use', delay: 150 },
  { label: 'Low vegetable intake', delay: 300 },
];

const PROTECTIVE_FACTORS = [
  { label: 'Regular exercise', delay: 100 },
  { label: 'Lives in rural area', delay: 250 },
  { label: 'Drinks tea', delay: 400 },
];

// ─── Sub-components ─────────────────────────────────────────────────────────

function CancerGrid({
  selectedCancer,
  visibleCount,
}: {
  selectedCancer?: string;
  visibleCount: number;
}) {
  return (
    <div className="mt-6 grid grid-cols-5 gap-2">
      {CANCER_TYPES.map((cancer, i) => {
        const isSelected = cancer.slug === selectedCancer;
        const isVisible = i < visibleCount;

        return (
          <div
            key={cancer.slug}
            className={`flex flex-col items-center gap-1 transition-all duration-500 ${
              isVisible ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
            }`}
            style={{ transitionDelay: `${i * 40}ms` }}
          >
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-500 ${
                isSelected
                  ? 'bg-[rgba(124,241,199,0.25)] ring-2 ring-[#7CF1C7] ring-offset-1 ring-offset-transparent'
                  : 'bg-white/10'
              }`}
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              <img
                src={`/images/icons/${cancer.icon}.svg`}
                alt={cancer.label}
                className="h-7 w-7"
                style={{ filter: isSelected ? 'brightness(0) invert(1)' : 'brightness(0) invert(0.7)' }}
                draggable={false}
              />
            </div>
            <span
              className={`text-[10px] leading-tight transition-colors duration-500 ${
                isSelected ? 'font-medium text-[#7CF1C7]' : 'text-white/50'
              }`}
            >
              {cancer.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function CancerInterestInterstitial({ selectedCancer }: CancerInterestInterstitialProps) {
  const [phase, setPhase] = useState(0);
  const [gridVisible, setGridVisible] = useState(0);
  const [tilesVisible, setTilesVisible] = useState(false);
  const [fadeState, setFadeState] = useState<'in' | 'out' | 'visible'>('visible');

  const advanceToPhase = useCallback((next: number) => {
    setFadeState('out');
    setTimeout(() => {
      setPhase(next);
      setFadeState('in');
      setTimeout(() => setFadeState('visible'), 50);
    }, 400);
  }, []);

  // Phase 0: cancer grid fills in, then auto-advance
  useEffect(() => {
    if (phase !== 0) return;
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (let i = 1; i <= CANCER_TYPES.length; i++) {
      timers.push(setTimeout(() => setGridVisible(i), 200 + i * 60));
    }

    const totalGridTime = 200 + CANCER_TYPES.length * 60 + 2000;
    timers.push(setTimeout(() => advanceToPhase(1), totalGridTime));

    return () => timers.forEach(clearTimeout);
  }, [phase, advanceToPhase]);

  // Phase 1: reveal risk driver/protective factor tiles
  useEffect(() => {
    if (phase !== 1) return;
    const timer = setTimeout(() => setTilesVisible(true), 600);
    return () => clearTimeout(timer);
  }, [phase]);

  const fadeClass =
    fadeState === 'in'
      ? 'opacity-0 translate-y-4'
      : fadeState === 'out'
        ? 'opacity-0 -translate-y-4'
        : 'opacity-100 translate-y-0';

  return (
    <div className="mx-auto w-full max-w-md">
      <div className={`transition-all duration-400 ${fadeClass}`}>
        {/* ── Phase 0: Cancer grid ──────────────────────────────────────── */}
        {phase === 0 && (
          <div className="space-y-2 px-4 text-center">
            <p className="text-xl font-semibold leading-snug text-white md:text-2xl">
              Got it. We assess your personalized risk for{' '}
              <span className="text-[#7CF1C7]">over 20 cancers</span>.
            </p>
            <CancerGrid selectedCancer={selectedCancer} visibleCount={gridVisible} />
          </div>
        )}

        {/* ── Phase 1: Risk drivers & protective factors ────────────────── */}
        {phase === 1 && (
          <div className="space-y-6 px-4 text-center">
            <h2 className="text-2xl font-semibold leading-snug text-white md:text-3xl">
              And show exactly what&apos;s influencing your risk.
            </h2>

            <div className="relative mt-8 flex min-h-[220px] items-center justify-center gap-4">
              {/* Left column: Risk Drivers */}
              <div className="flex flex-1 flex-col items-stretch gap-2">
                <span
                  className="mb-2 text-xs font-semibold"
                  style={{ color: 'rgba(255,100,80,0.85)' }}
                >
                  Risk Drivers
                </span>
                {RISK_DRIVERS.map((item) => (
                  <div
                    key={item.label}
                    className="w-full rounded-lg px-3 py-2 text-xs font-medium transition-all duration-500"
                    style={{
                      backgroundColor: 'rgba(255,100,80,0.12)',
                      border: '1px solid rgba(255,100,80,0.25)',
                      color: 'rgba(255,255,255,0.85)',
                      opacity: tilesVisible ? 1 : 0,
                      transform: tilesVisible ? 'translateX(0)' : 'translateX(-30px)',
                      transitionDelay: `${item.delay}ms`,
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>

              {/* Center: Risk Profile circle */}
              <div
                className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full text-center transition-all duration-700"
                style={{
                  backgroundColor: 'rgba(124, 241, 199, 0.12)',
                  border: '2px solid #7CF1C7',
                  opacity: tilesVisible ? 1 : 0.3,
                  transform: tilesVisible ? 'scale(1)' : 'scale(0.8)',
                }}
              >
                <span className="text-[10px] font-semibold leading-tight text-white">
                  Your Risk
                  <br />
                  Profile
                </span>
              </div>

              {/* Right column: Protective Factors */}
              <div className="flex flex-1 flex-col items-stretch gap-2">
                <span className="mb-2 text-xs font-semibold" style={{ color: '#7CF1C7' }}>
                  Protective Factors
                </span>
                {PROTECTIVE_FACTORS.map((item) => (
                  <div
                    key={item.label}
                    className="w-full rounded-lg px-3 py-2 text-xs font-medium transition-all duration-500"
                    style={{
                      backgroundColor: 'rgba(124, 241, 199, 0.10)',
                      border: '1px solid rgba(124, 241, 199, 0.25)',
                      color: 'rgba(255,255,255,0.85)',
                      opacity: tilesVisible ? 1 : 0,
                      transform: tilesVisible ? 'translateX(0)' : 'translateX(30px)',
                      transitionDelay: `${item.delay}ms`,
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
